import { LoadingIndicatorComponent } from '@frontendlib/components/components/loading-indicator/loading-indicator.component';
import { NoContentStubComponent } from '@frontendlib/components/components/no-content-stub/no-content-stub.component';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  afterNextRender,
  Injector,
  ViewChild,
  ElementRef,
  inject,
  Input,
  OnChanges,
  output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import {
  BaseStitchListComponent,
  type StitchListItem,
  type ItemsResponse,
} from '@modules/common/components/list/base-stitch-list.component';
import { ListLoadErrorComponent } from '@modules/common/components/list/list-load-error/list-load-error.component';
import { Pagination2Component } from '@modules/common/components/pagination/pagination2.component';
import { ScrollToPosition } from '@modules/common/services/scroll-to-index.injection-token';
import { owningTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { LinkInfoDeckComponent } from '@modules/elements/components/link-info/link-info-deck/link-info-deck.component';
import { StitchType } from '@sharedlib/common/types/StitchType';
import type { FormSummaryDto } from '@sharedlib/dtos/dto/forms-app/form.dto';
import type { FormFolderSummaryDto } from '@sharedlib/dtos/dto/forms-app/form.dto';
import type { Stitch } from '@modules/common/types/stitch';
import { DragNDropModule } from '@modules/drag-n-drop/drag-n-drop.module';
import { SelectableListDirective } from '@modules/drag-n-drop/directives/selectable-list.directive';
import type { SelectableOpenEvent } from '@modules/drag-n-drop/types/selectable-open-event';
import { ListSelectionBarComponent } from '@modules/common/components/list/list-selection-bar/list-selection-bar.component';
import { FormsLibraryCardComponent } from './forms-library-card.component';
import { ClickDirective } from '@modules/utils/directives/click/click.directive';
import { BehaviorSubject, finalize, type Observable } from 'rxjs';
import { FormStitchFilters, FormsStitchService } from '../services/forms.service';
import { FormFolderStitchFilters, FormFoldersStitchService } from '../services/form-folders.service';

type FormsHubSummary = FormSummaryDto | FormFolderSummaryDto;

/** Forms adopts the native list loading/error/pagination/refresh contract, as SheetsList does. Cards are the shared Boards renderer. */
@Component({
  selector: 'app-forms-hub-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LoadingIndicatorComponent, NoContentStubComponent, CommonModule, ListLoadErrorComponent, Pagination2Component, LinkInfoDeckComponent, ClickDirective, DragNDropModule, ListSelectionBarComponent, FormsLibraryCardComponent],
  providers: [{ provide: ScrollToPosition, useFactory: () => new BehaviorSubject<number | null>(null) }],
  templateUrl: './forms-hub-list.component.html',
  host: { class: 'flex h-full flex-col' },
})
export class FormsHubListComponent extends BaseStitchListComponent<FormsHubSummary> implements OnChanges {
  @Input() public override filters: Partial<FormStitchFilters | FormFolderStitchFilters> = {};
  @Input() public tenantId: string | undefined;
  @Input() public tenantItems: ReadonlyArray<Stitch> = [];
  @Input() public kind: 'form' | 'folder' = 'form';
  @Input() public kpInteractions = false;
  @Input() public libraryInteractions = false;
  @Input() public selectAllRequest = 0;
  public readonly selectionApplied = output<void>();
  public readonly itemActivate = output<string>();
  public readonly itemOpen = output<string>();
  public items: StitchListItem<FormsHubSummary>[] = [];
  @ViewChildren('hubItem', { read: ElementRef }) public override itemElements!: QueryList<
    ElementRef<HTMLElement>
  >;
  @ViewChild('nativeSelection') private nativeSelection: SelectableListDirective<FormsHubSummary> | undefined;
  private readonly renderInjector = inject(Injector);
  private handledSelection = 0;
  private readonly forms = inject(FormsStitchService);
  private readonly folders = inject(FormFoldersStitchService);
  private readonly changeDetector = inject(ChangeDetectorRef);
  public ngOnChanges(changes: SimpleChanges): void {
    if ('filters' in changes || 'tenantId' in changes || 'tenantItems' in changes || 'kind' in changes)
      this.resetItems();
    if ('selectAllRequest' in changes && this.selectAllRequest > 0) this.applySelectionAfterRender();
  }
  protected override afterItemsLoaded(): void { if (this.libraryInteractions) this.applySelectionAfterRender(); }
  private applySelectionAfterRender(): void {
    const request = this.selectAllRequest, tenant = this.tenantId, filters = JSON.stringify(this.filters);
    if (!request || request <= this.handledSelection) return;
    afterNextRender(() => {
      if (request <= this.handledSelection || request !== this.selectAllRequest || tenant !== this.tenantId || filters !== JSON.stringify(this.filters) || this.loading || !this.nativeSelection) return;
      this.handledSelection = request; this.nativeSelection.selectableService.triggerSelectAll(); this.selectionApplied.emit();
    }, { injector: this.renderInjector });
  }
  protected activateLibrary(event: SelectableOpenEvent<FormsHubSummary>): void {
    if (event.item?.data.itemType === StitchType.FORM) this.itemActivate.emit(event.item.data.id);
  }
  protected override getStitchType(): StitchType {
    return this.kind === 'folder' ? StitchType.FORM_FOLDER : StitchType.FORM;
  }
  protected override stitchSignalSpaceIds(): ReadonlyArray<string> {
    return [
      ...new Set(
        [this.spaceContextStore.contextId(), this.tenantId, ...this.tenantItems.map((item) => item.accountId)].filter(
          (id): id is string => !!id,
        ),
      ),
    ];
  }
  protected override getItems(offset: number, limit: number): Observable<ItemsResponse<FormsHubSummary>> {
    const request = {
      ...this.filters,
      ...(this.filters.archived !== undefined || !this.filters.deleted ? { archived: this.filters.archived ?? false } : {}),
      deleted: this.filters.deleted ?? false,
      short: true as const,
      offset,
      limit,
    };
    const config = this.tenantId
      ? { requestContext: owningTenantContext(this.tenantId) }
      : { tenantItems: this.tenantItems };
    this.changeDetector.markForCheck();
    const result: Observable<ItemsResponse<FormsHubSummary>> =
      this.kind === 'folder' ? this.folders.search(request, config) : this.forms.search(request, config);
    return result.pipe(finalize(() => this.changeDetector.markForCheck()));
  }
  protected activateKey(event: Event, id: string): void {
    if (!(event instanceof KeyboardEvent)) return;
    event.preventDefault();
    if (!event.repeat) this.itemActivate.emit(id);
  }
}
